import { Autocomplete, Box, Divider, InputAdornment, Link, Stack, TextField } from '@mui/material';
import { type FlowLlmPromptBlock } from 'flow-schema';
import { type HTMLAttributes, type Key, useId, useState } from 'react';

import { FormField, Text, isDefined } from 'common';
import { LlmModelModality, LlmModelName } from 'llm-model-registry';

import { LlmProviderLogo } from './LlmProviderLogo';
import {
  LLM_MODEL_PICKER_RECOMMENDED_DESCRIPTION_SINGULAR,
  LLM_MODEL_PICKER_RECOMMENDED_GROUP,
  LLM_MODEL_PICKER_SINGULAR_AUTO_ID,
  type LlmModelPickerOption,
  buildLlmModelPickerAutoOption,
  buildLlmModelPickerOptions,
  getLlmModelPickerGroupHeader,
  getLlmModelPickerOptionSearchText,
  getLlmModelPickerSelectedModelNotice,
  isLlmModelPickerAutoOptionId,
} from './modelPicker';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField.ts';

const RATE_CARD_URL = 'https://www.notablehealth.com/files/platform-rate-card';

const DEFAULT_HELPER_TEXT = (
  <>
    {
      "Select which LLM model to run. Intelligence is independently graded by Artificial Analysis. Billed at the model's per-token rate. "
    }
    <Link href={RATE_CARD_URL} target="_blank" rel="noopener noreferrer">
      Rate Card
    </Link>
  </>
);

interface LlmModelPickerFieldProps {
  fieldNamePrefix: string;
}

export function LlmModelPickerField(props: LlmModelPickerFieldProps) {
  const { fieldNamePrefix } = props;
  const modelFieldId = useId();

  const { value: selectedModelId, setValue: setModelName } = useSelectedNodeField<
    FlowLlmPromptBlock['config']['modelName']
  >(`${fieldNamePrefix}.modelName`);
  const { value: fileReference, setValue: setFileReference } = useSelectedNodeField<
    FlowLlmPromptBlock['config']['fileReference']
  >(`${fieldNamePrefix}.fileReference`);

  // The picked model lives in React Flow's controlled node store, not local state, so a
  // pick round-trips (setModelName -> updateNodeData -> parent setNodes -> nodes prop ->
  // useNodesData -> selectedModelId) over a few renders. Hold the pick until the store
  // moves off the value it had at pick time — whether it caught up to our pick or changed
  // elsewhere (undo, switching blocks) — otherwise the controlled Autocomplete would snap
  // back to the previous model in between.
  const [pendingSelectedModel, setPendingSelectedModel] = useState<{
    option: LlmModelPickerOption;
    baseModelId: LlmModelName | undefined;
  } | null>(null);
  if (isDefined(pendingSelectedModel) && selectedModelId !== pendingSelectedModel.baseModelId) {
    setPendingSelectedModel(null);
  }

  const options = buildLlmModelPickerOptions({ selectedModelId });

  // "Auto" persists as the legacy `notable-default` model id (mapped to Gemini
  // 2.5 Flash by the backend), so surface that saved value as the Auto option.
  // Typed as a string because option ids are strings ('auto' or a model id); this
  // is an option-lookup key, not a model enum.
  const effectiveSelectedId: string | undefined =
    selectedModelId === LlmModelName.notable_default
      ? LLM_MODEL_PICKER_SINGULAR_AUTO_ID
      : selectedModelId;

  const storeSelectedOption = options.find((option) => option.id === effectiveSelectedId);
  // The model is required (new blocks default to Auto), so fall back to Auto when a
  // saved id can't be resolved — keeps the control in a valid, non-clearable state.
  const selectedOption =
    pendingSelectedModel?.option ?? storeSelectedOption ?? buildLlmModelPickerAutoOption();
  const displayedModel = selectedOption.model;

  const selectedModelNotice = getLlmModelPickerSelectedModelNotice(displayedModel);

  return (
    <FormField
      id={modelFieldId}
      label="LLM Model"
      helperText={selectedModelNotice ?? DEFAULT_HELPER_TEXT}
    >
      <Autocomplete
        id={modelFieldId}
        options={options}
        value={selectedOption}
        disableClearable
        groupBy={(option) => option.group ?? ''}
        getOptionLabel={(option) => option.model.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        selectOnFocus
        filterOptions={(availableOptions, state) => {
          const query = state.inputValue.trim().toLowerCase();
          if (query.length === 0) {
            return availableOptions;
          }
          if (state.inputValue === selectedOption.model.name) {
            return availableOptions;
          }
          return availableOptions.filter((option) =>
            getLlmModelPickerOptionSearchText(option).includes(query)
          );
        }}
        onChange={(_event, newValue) => {
          setPendingSelectedModel({ option: newValue, baseModelId: selectedModelId });
          if (isLlmModelPickerAutoOptionId(newValue.id)) {
            setModelName(LlmModelName.notable_default);
          } else {
            setModelName(newValue.model.id);
          }
          const supportsFileContext = newValue.model.inputModalities.includes(
            LlmModelModality.File
          );
          if (isDefined(fileReference) && !supportsFileContext) {
            setFileReference(undefined);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: isDefined(selectedOption.model.provider) ? (
                  <InputAdornment
                    position="start"
                    // Nudge the logo right so it aligns with the input text. MUI only sets
                    // `marginRight` on a start adornment, so this has nothing to override.
                    sx={{ marginLeft: 1 }}
                  >
                    <LlmProviderLogo provider={selectedOption.model.provider} size={16} />
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />
        )}
        renderOption={(optionProps, option) => {
          // MUI types renderOption's `key` as `any`; narrow it so the spread and the
          // `key` prop below don't trip our no-unsafe-* lint rules.
          const { key, ...listItemProps } = optionProps as {
            key?: Key;
          } & HTMLAttributes<HTMLLIElement>;
          return (
            <li key={key} {...listItemProps}>
              <Stack width="100%">
                <Stack direction="row" alignItems="center" gap={0.75}>
                  {isDefined(option.model.provider) && (
                    <LlmProviderLogo provider={option.model.provider} size={16} />
                  )}
                  <Text variant="body2" bold>
                    {option.model.name}
                  </Text>
                </Stack>
                {option.id === LLM_MODEL_PICKER_SINGULAR_AUTO_ID && (
                  <Text variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {LLM_MODEL_PICKER_RECOMMENDED_DESCRIPTION_SINGULAR}
                  </Text>
                )}
              </Stack>
            </li>
          );
        }}
        // Group rendering follows MUI's "Grouped" Autocomplete example: each group is an
        // <li> holding the header plus the options wrapped in a padding-less <ul>, and the
        // sticky header is offset by a negative `top` (theme.spacing(-1) = -8px) to cancel
        // the listbox's 8px vertical padding so it pins flush while scrolling.
        // See https://mui.com/material-ui/react-autocomplete/#grouped
        renderGroup={(params) => {
          if (params.group === LLM_MODEL_PICKER_RECOMMENDED_GROUP) {
            return (
              <Box component="li" key={params.key}>
                <Box component="ul" sx={{ padding: 0 }}>
                  {params.children}
                </Box>
              </Box>
            );
          }
          const header = getLlmModelPickerGroupHeader(params.group);
          return (
            <Box component="li" key={params.key} sx={{ paddingTop: 2.5 }}>
              <Divider />
              <Box
                sx={(theme) => ({
                  position: 'sticky',
                  top: theme.spacing(-1),
                  backgroundColor: 'background.paper',
                  zIndex: 1,
                  paddingX: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                })}
              >
                <Text variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  <strong>{header.title}</strong>
                  {isDefined(header.description) && ` - ${header.description}`}
                </Text>
              </Box>
              <Box component="ul" sx={{ padding: 0 }}>
                {params.children}
              </Box>
            </Box>
          );
        }}
      />
    </FormField>
  );
}
